import { InjectionToken } from '@angular/core';

/**
 * Per-app configuration for {@link AuthService} and related guards/interceptor.
 * Provide via {@link provideAuth}.
 */
export interface AuthConfig {
  /** Application code on login/refresh/logout (e.g. `plt`, `inv`). */
  application: string;
  /** localStorage key for non-sensitive session meta (tenant/email for silent refresh). */
  sessionMetaKey: string;
  /**
   * Optional legacy localStorage key cleared on persist/logout
   * (migration from older JWT-in-localStorage clients).
   */
  legacySessionKey?: string;
  /** Login route path. Default `/login`. */
  loginUrl?: string;
  /** TOTP challenge route after password when enrolled. Default `/login/totp`. */
  totpChallengeUrl?: string;
  /** TOTP enrollment route. Default `/security/totp`. */
  totpSetupUrl?: string;
  /** Forbidden route path. Default `/forbidden`. */
  forbiddenUrl?: string;
  /**
   * Deployed Platform API origin (e.g. `https://api.dev.plt.platepath.com`).
   * Empty locally so `/platform/auth/*` stays same-origin for the Angular proxy.
   */
  apiBaseUrl?: string;
}

/** Normalized config with defaults applied. */
export interface ResolvedAuthConfig {
  application: string;
  sessionMetaKey: string;
  legacySessionKey: string | null;
  loginUrl: string;
  totpChallengeUrl: string;
  totpSetupUrl: string;
  forbiddenUrl: string;
  apiBaseUrl: string;
}

export const AUTH_CONFIG = new InjectionToken<ResolvedAuthConfig>('AUTH_CONFIG');

export function resolveAuthConfig(config: AuthConfig): ResolvedAuthConfig {
  if (!config.application?.trim()) {
    throw new Error('@rind/auth: AuthConfig.application is required.');
  }
  if (!config.sessionMetaKey?.trim()) {
    throw new Error('@rind/auth: AuthConfig.sessionMetaKey is required.');
  }

  return {
    application: config.application.trim(),
    sessionMetaKey: config.sessionMetaKey.trim(),
    legacySessionKey: config.legacySessionKey?.trim() || null,
    loginUrl: config.loginUrl?.trim() || '/login',
    totpChallengeUrl: config.totpChallengeUrl?.trim() || '/login/totp',
    totpSetupUrl: config.totpSetupUrl?.trim() || '/security/totp',
    forbiddenUrl: config.forbiddenUrl?.trim() || '/forbidden',
    apiBaseUrl: config.apiBaseUrl?.replace(/\/$/, '').trim() || '',
  };
}
